import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';
import {
  AccountingNote,
  GetNotesResponse,
  CreateNoteRequest,
  CreateNoteResponse,
  UpdateNoteRequest,
  UpdateNoteResponse,
} from '../types/accounting-notes';

/**
 * Hook to fetch notes for an entity (invoice or bill)
 */
export const useAccountingNotes = (
  entityType: 'invoice' | 'bill',
  entityId: string,
  enabled = true
) => {
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<AccountingNote[], Error>({
    queryKey: ['accountingNotes', entityType, entityId],
    queryFn: async (): Promise<AccountingNote[]> => {
      const url = `${API_URL}/api/accounting/admin/notes/list?entityType=${entityType}&entityId=${entityId}`;
      const response = await authenticatedFetch(url);

      if (!response || !response.ok) {
        const errorText = await response?.text().catch(() => 'Unknown error');
        throw new Error(`Failed to fetch notes: ${errorText}`);
      }

      const data: GetNotesResponse = await response.json();
      return data.notes || [];
    },
    enabled: enabled && !!entityId,
    staleTime: 30000, // Cache for 30 seconds
  });
};

/**
 * Hook to fetch notes for multiple entities (used in reconciliation detail)
 */
export const useMultipleEntityNotes = (
  invoiceIds: string[],
  billIds: string[],
  enabled = true
) => {
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<AccountingNote[], Error>({
    queryKey: ['multipleEntityNotes', invoiceIds.sort(), billIds.sort()],
    queryFn: async (): Promise<AccountingNote[]> => {
      const allNotes: AccountingNote[] = [];

      // Fetch notes for all invoices
      for (const entityId of invoiceIds) {
        const url = `${API_URL}/api/accounting/admin/notes/list?entityType=invoice&entityId=${entityId}`;
        const response = await authenticatedFetch(url);
        if (response && response.ok) {
          const data: GetNotesResponse = await response.json();
          allNotes.push(...(data.notes || []));
        }
      }

      // Fetch notes for all bills
      for (const entityId of billIds) {
        const url = `${API_URL}/api/accounting/admin/notes/list?entityType=bill&entityId=${entityId}`;
        const response = await authenticatedFetch(url);
        if (response && response.ok) {
          const data: GetNotesResponse = await response.json();
          allNotes.push(...(data.notes || []));
        }
      }

      // Sort by created date descending
      return allNotes.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    },
    enabled: enabled && (invoiceIds.length > 0 || billIds.length > 0),
    staleTime: 30000,
  });
};

/**
 * Hook to create a new note
 */
export const useCreateNote = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<AccountingNote, Error, CreateNoteRequest>({
    mutationFn: async (noteData: CreateNoteRequest): Promise<AccountingNote> => {
      const url = `${API_URL}/api/accounting/admin/notes`;
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to create note');
      }

      const data: CreateNoteResponse = await response.json();
      return data.note;
    },
    onSuccess: (_, variables) => {
      // Invalidate the notes query for this entity
      queryClient.invalidateQueries({
        queryKey: ['accountingNotes', variables.entityType, variables.entityId],
      });
      // Also invalidate multiple entity notes queries
      queryClient.invalidateQueries({
        queryKey: ['multipleEntityNotes'],
      });
    },
  });
};

/**
 * Hook to update an existing note
 */
export const useUpdateNote = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    AccountingNote,
    Error,
    { noteId: number; content: string; entityType: 'invoice' | 'bill'; entityId: string }
  >({
    mutationFn: async ({ noteId, content }): Promise<AccountingNote> => {
      const url = `${API_URL}/api/accounting/admin/notes/${noteId}`;
      const response = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content } as UpdateNoteRequest),
      });

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to update note');
      }

      const data: UpdateNoteResponse = await response.json();
      return data.note;
    },
    onSuccess: (_, variables) => {
      // Invalidate the notes query for this entity
      queryClient.invalidateQueries({
        queryKey: ['accountingNotes', variables.entityType, variables.entityId],
      });
      // Also invalidate multiple entity notes queries
      queryClient.invalidateQueries({
        queryKey: ['multipleEntityNotes'],
      });
    },
  });
};
